import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Clock, Download, Lock, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useOrgBillingAccess } from '@/hooks/useOrgSubscription';
import { useOrgDeletionRequest } from '@/hooks/useDataExport';
import { getSubscriptionAccess, TRIAL_BANNER_DAYS } from '@/lib/subscription-access';
import { Button } from '@/components/ui/button';
import { adminButton } from '@/components/admin/ui';

const BILLING_PATH = '/admin/billing';
/** "Your data" stays open whatever the plan: owners can always export. */
const DATA_PATH = '/admin/data';

function formatDay(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
}

/**
 * Wraps every admin page. When a business's free trial has ended without a
 * plan, the admin is replaced by a "choose a plan" screen (Billing stays
 * usable). Their public website is not affected. Platform admins are never
 * locked out, and a trial in its last 14 days shows a countdown banner.
 */
export function SubscriptionGate({ children }: { children: ReactNode }) {
  const { isPlatformAdmin, isSuperAdmin, staffRole, isOrgOwner } = useAuth();
  const { orgId } = useAdminOrg();
  const { pathname } = useLocation();
  const exempt = isPlatformAdmin || isSuperAdmin || staffRole === 'super_admin';
  const { data: subscription, isLoading } = useOrgBillingAccess(exempt ? null : orgId);
  const { data: deletion } = useOrgDeletionRequest(exempt ? null : orgId);

  if (exempt || isLoading) return <>{children}</>;

  const onData = pathname.startsWith(DATA_PATH);

  // The owner has deleted the business: only "Your data" stays usable until the purge.
  if (deletion && !onData) {
    const purgeDay = formatDay(deletion.purge_after);
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-16 text-center md:py-24" data-testid="org-deleted-lock">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
          <Trash2 className="h-5 w-5" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-[22px] font-semibold tracking-tight text-foreground">This business is being deleted</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
          The website is offline and everything will be permanently removed{purgeDay ? ` on ${purgeDay}` : ''}. You can
          still download a copy of the data until then.
        </p>
        <Button asChild className={`mt-6 ${adminButton.primary}`}>
          <Link to={DATA_PATH}>
            <Download /> Download your data
          </Link>
        </Button>
      </div>
    );
  }

  const access = getSubscriptionAccess(subscription);
  const onBilling = pathname.startsWith(BILLING_PATH) || onData;
  const canChoosePlan = isOrgOwner || staffRole === 'owner';

  if (access.state === 'locked' && !onBilling) {
    return (
      <div className="mx-auto flex max-w-lg flex-col items-center px-6 py-16 text-center md:py-24" data-testid="trial-ended-lock">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-primary">
          <Lock className="h-5 w-5" aria-hidden="true" />
        </span>
        <h1 className="mt-5 text-[22px] font-semibold tracking-tight text-foreground">Your free trial has ended</h1>
        {canChoosePlan ? (
          <>
            <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
              Choose a plan to pick up where you left off. Your website and booking page are still live, and none of
              your bookings, customers or settings have been lost.
            </p>
            <Button asChild className={`mt-6 ${adminButton.primary}`}>
              <Link to={BILLING_PATH}>
                Choose a plan <ArrowRight />
              </Link>
            </Button>
            <p className="mt-3 text-[12.5px] text-muted-foreground">From £9 a month. Cancel any time.</p>
          </>
        ) : (
          <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
            Ask the business owner to choose a plan to open the admin again. The website and booking page are still
            live.
          </p>
        )}
      </div>
    );
  }

  const showCountdown =
    access.state === 'trial' &&
    canChoosePlan &&
    !onBilling &&
    access.trialDaysLeft !== null &&
    access.trialDaysLeft <= TRIAL_BANNER_DAYS;

  return (
    <>
      {showCountdown && (
        <div
          role="region"
          aria-label="Free trial"
          data-testid="trial-countdown"
          className="border-b border-border bg-secondary/60 px-4 py-2.5 text-secondary-foreground"
        >
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]">
            <Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <p className="min-w-0 flex-1">
              <span className="font-semibold">
                {access.trialDaysLeft === 1 ? '1 day' : `${access.trialDaysLeft} days`} left of your free trial.
              </span>{' '}
              Choose a plan by {formatDay(access.trialEndsAt)} to keep using your admin. You won&rsquo;t be charged
              until then.
            </p>
            <Link to={BILLING_PATH} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              Choose a plan <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}
      {children}
    </>
  );
}
